import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import { WorkTask } from '../../types/task';
import { useAuth } from '../../contexts/auth';
import {
  getWorkTaskByKey,
} from '../../api/workTask';
import './work-task-details.scss';
import { WorkOrderTable } from '../../components/library/work-order-tabel/WorkOrderTable';
import { WorkTaskFiles } from '../../components/library/work-task-files/WorkTaskFiles';
import moment from 'moment';
import { WORK_TASK_STATUS_LABELS } from '../../shared/constants';

export interface State {
  workTaskOid: string,
  backTo: string,
  relationOid: string
}

const STATUS_COLORS: Record<string, string> = {
  Open: '#2196f3',
  Planned: '#ff9800',
  WorkOrderCompleted: '#4caf50',
  InvoiceSent: '#9c27b0',
};

export const WorkTaskDetails = () => {
  const [workTask, setWorkTask] = useState<WorkTask>();
  const [isLoading, setIsLoading] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const state = useLocation().state as State;
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(() => {
    setIsLoading(true);
    getWorkTaskByKey(state.workTaskOid, user?.token)
      .then((data) => {
        setWorkTask(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const refresh = useCallback(() => {
    loadData();
  }, []);

  const goBackClick = useCallback(() => {
    if (state.backTo === 'workTasksList') {
      navigate('/work-tasks');
    } else if (state.backTo === 'crmDetails') {
      navigate('/crm-contact-details', { state: { relationOid: state.relationOid, backTo: 'crmList' } });
    } else if (state.backTo === 'workTasksPlanning') {
      navigate('/work-tasks-planning');
    } else if (state.backTo === 'analyticsDashboard') {
      navigate('/analytics-dashboard');
    }
  }, []);

  const statusColor = STATUS_COLORS[workTask?.Status ?? ''] ?? '#9e9e9e';
  const statusLabel = WORK_TASK_STATUS_LABELS[workTask?.Status ?? ''] ?? workTask?.Status ?? '';
  const employeeNames = workTask?.Employees?.map(e => e.FullName).join(', ') ?? '-';
  const formatDate = (d: Date | string | undefined) => d ? moment(d).format('DD-MM-YYYY HH:mm') : '-';

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-worktask-details'>
        <Toolbar className='toolbar-details'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={goBackClick} />
          </ToolbarItem>
          <ToolbarItem location='before'>
            <span className='toolbar-title'>{ workTask?.ReferenceNumber ? `${workTask.ReferenceNumber} — ` : '' }{ workTask?.TaskDescription ?? 'Laden...' }</span>
          </ToolbarItem>
          <ToolbarItem location='after' locateInMenu='auto'>
            <Button text='Vernieuwen' icon='refresh' stylingMode='text' onClick={refresh} />
          </ToolbarItem>
        </Toolbar>

        { isLoading ? (
          <div className='loading-placeholder'>Laden...</div>
        ) : workTask ? (
          <>
            { /* ── Summary banner ── */}
            <div className='summary-banner'>
              <div className='banner-item'>
                <span className='status-badge' style={ { backgroundColor: statusColor } }>
                  {statusLabel}
                </span>
              </div>
              <div className='banner-item'>
                <i className='dx-icon dx-icon-user banner-icon' />
                {workTask.Relation ? (
                  <span
                    className='relation-link'
                    onClick={() => navigate('/crm-contact-details', { state: { relationOid: workTask.Relation!.Oid, backTo: 'workTaskDetails', workTaskOid: state.workTaskOid } })}
                  >
                    {workTask.Relation.Name}
                  </span>
                ) : (
                  <span>-</span>
                )}
              </div>
              <div className='banner-item'>
                <i className='dx-icon dx-icon-event banner-icon' />
                <span>{formatDate(workTask.TaskStartDate)}</span>
                <span className='banner-separator'>→</span>
                <span>{formatDate(workTask.TaskEndDate)}</span>
              </div>
              <div className='banner-item'>
                <i className='dx-icon dx-icon-group banner-icon' />
                <span>{employeeNames}</span>
              </div>
              <button
                className='banner-expand-btn'
                onClick={() => setDetailsExpanded(!detailsExpanded)}
              >
                <i className={`dx-icon dx-icon-${detailsExpanded ? 'chevronup' : 'chevrondown'}`} />
                <span>{detailsExpanded ? 'Verbergen' : 'Details'}</span>
              </button>
            </div>

            { /* ── Stacked layout ── */}
            <div className='panels'>
              { /* Details card (collapsible) */}
              { detailsExpanded && (
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-info card-header-icon' />
                  <span>Details</span>
                </div>
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='info-label'>Opdracht</span>
                    <span className='info-value'>{workTask.TaskDescription ?? '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Werkzaamheden</span>
                    <span className='info-value'>{workTask.Activity?.Name ?? '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Relatie</span>
                    <span className='info-value accent'>
                      {workTask.Relation ? (
                        <span
                          className='relation-link'
                          onClick={() => navigate('/crm-contact-details', { state: { relationOid: workTask.Relation!.Oid, backTo: 'workTaskDetails', workTaskOid: state.workTaskOid } })}
                        >
                          {workTask.Relation.Name}
                        </span>
                      ) : (
                        <span>-</span>
                      )}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Status</span>
                    <span className='info-value'>
                      <span className='status-dot' style={ { backgroundColor: statusColor } } />
                      {statusLabel}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Medewerkers</span>
                    <span className='info-value'>{employeeNames}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Start</span>
                    <span className='info-value'>{formatDate(workTask.TaskStartDate)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Einde</span>
                    <span className='info-value'>{formatDate(workTask.TaskEndDate)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Aangemaakt</span>
                    <span className='info-value'>{formatDate(workTask.CreatedAt)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Bijgewerkt</span>
                    <span className='info-value'>{formatDate(workTask.UpdatedAt)}</span>
                  </div>
                </div>
              </div>
              )}

              { /* Remarks card */}
              { workTask.TaskRemarks && (
                <div className='dx-card info-card'>
                  <div className='card-header'>
                    <i className='dx-icon dx-icon-comment card-header-icon' />
                    <span>Opmerkingen</span>
                  </div>
                  <div className='remarks-content'>
                    {workTask.TaskRemarks}
                  </div>
                </div>
              )}

              { /* Werkbonnen card */}
              <div className='dx-card details-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-paste card-header-icon' />
                  <span>Werkbonnen</span>
                </div>
                <WorkOrderTable workTaskOid={state.workTaskOid} isLoading={isLoading} />
              </div>

              { /* Bestanden card */}
              <div className='dx-card details-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-folder card-header-icon' />
                  <span>Bestanden</span>
                </div>
                <WorkTaskFiles workTaskOid={state.workTaskOid} />
              </div>
            </div>
          </>
        ) : (
          <div className='loading-placeholder'>Opdracht niet gevonden</div>
        )}
      </div>
    </ScrollView>
  );
};
